import * as Dialog from '@radix-ui/react-dialog';
import type { Behavior, TransformChainStep, CrossFieldOffender } from '@capture/shared';
import type { OtherDestinationSummary } from '../hooks/usePromoteToCanonicalContext';

export type DialogState = 'mergeable' | 'hard-block' | 'named-chain-error';

export interface NamedChainError {
  namedChainId: string;
  status: number;
}

export interface PromoteConfirm {
  position: 'append' | 'prepend';
}

interface Props {
  open: boolean;
  state: DialogState;
  sourceFieldName: string;
  newSteps: TransformChainStep[];
  existingPostOcr: Behavior[];
  otherDestinations: OtherDestinationSummary[];
  crossFieldOffenders: CrossFieldOffender[];
  namedChainError: NamedChainError | null;
  onClose: () => void;
  onConfirm: (confirm: PromoteConfirm) => void;
  onRetry: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  zIndex: 200,
};

const contentStyle: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  background: 'var(--color-bg-primary, #0e0e0e)',
  border: '1px solid var(--color-border, #2a2a2a)',
  borderRadius: 6,
  padding: 24,
  zIndex: 201,
  minWidth: 480,
  maxWidth: 600,
};

const titleStyle: React.CSSProperties = {
  margin: '0 0 16px',
  fontSize: 'var(--text-sm, 0.875rem)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-primary, #f2f2f2)',
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 'var(--text-xs, 0.8125rem)',
  color: 'var(--color-text-muted, #888)',
  marginBottom: 4,
};

const listBoxStyle: React.CSSProperties = {
  border: '1px solid var(--color-border, #2a2a2a)',
  borderRadius: 4,
  padding: '8px 12px',
  marginBottom: 12,
  background: 'var(--color-bg-secondary, #131313)',
  fontSize: 'var(--text-xs, 0.8125rem)',
};

const cancelBtn: React.CSSProperties = {
  padding: '6px 14px',
  border: '1px solid var(--color-border, #2a2a2a)',
  borderRadius: 4,
  background: 'var(--color-bg-secondary, #131313)',
  color: 'var(--color-text-muted, #888)',
  cursor: 'pointer',
  fontSize: 'var(--text-xs, 0.8125rem)',
};

const primaryBtn: React.CSSProperties = {
  padding: '6px 14px',
  border: '1px solid var(--color-accent, #fc9430)',
  borderRadius: 4,
  background: 'var(--color-accent, #fc9430)',
  color: '#0e0e0e',
  cursor: 'pointer',
  fontSize: 'var(--text-xs, 0.8125rem)',
  fontWeight: 600,
};

const mutedParagraphStyle: React.CSSProperties = {
  fontSize: 'var(--text-xs, 0.8125rem)',
  color: 'var(--color-text-muted, #888)',
  margin: '0 0 16px',
};

const buttonRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
};

const listItemStyle: React.CSSProperties = {
  padding: '2px 0',
};

const secondaryBtn: React.CSSProperties = {
  padding: '6px 14px',
  border: '1px solid var(--color-border, #2a2a2a)',
  borderRadius: 4,
  background: 'var(--color-bg-tertiary, #1a1a1a)',
  color: 'var(--color-text-primary, #f2f2f2)',
  cursor: 'pointer',
  fontSize: 'var(--text-xs, 0.8125rem)',
  fontWeight: 500,
};

export function PromoteToCanonicalDialog({
  open, state, sourceFieldName, newSteps, existingPostOcr, otherDestinations,
  crossFieldOffenders, namedChainError, onClose, onConfirm, onRetry,
}: Props) {
  function handleOpenChange(o: boolean) {
    if (!o) onClose();
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay style={overlayStyle} />
        <Dialog.Content style={contentStyle} aria-describedby={undefined}>
          {state === 'hard-block' && (
            <HardBlockBody
              sourceFieldName={sourceFieldName}
              offenders={crossFieldOffenders}
              onClose={onClose}
            />
          )}
          {state === 'named-chain-error' && namedChainError && (
            <NamedChainErrorBody
              error={namedChainError}
              onClose={onClose}
              onRetry={onRetry}
            />
          )}
          {state === 'mergeable' && (
            <MergeableBody
              sourceFieldName={sourceFieldName}
              newSteps={newSteps}
              existingPostOcr={existingPostOcr}
              otherDestinations={otherDestinations}
              onClose={onClose}
              onConfirm={onConfirm}
            />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function HardBlockBody({
  sourceFieldName, offenders, onClose,
}: { sourceFieldName: string; offenders: CrossFieldOffender[]; onClose: () => void }) {
  return (
    <>
      <Dialog.Title style={titleStyle}>Cannot promote this chain</Dialog.Title>
      <p style={{ fontSize: 'var(--text-xs, 0.8125rem)', color: 'var(--color-text-muted, #888)', margin: '0 0 12px' }}>
        This chain has {offenders.length} step{offenders.length === 1 ? '' : 's'} that reference{offenders.length === 1 ? 's' : ''} other fields.
        Post-OCR behaviors run per-field with no cross-field timing guarantees, so the following can't promote
        from <code>{sourceFieldName}</code>:
      </p>
      <ul style={{ ...listBoxStyle, listStyle: 'none', margin: '0 0 12px', paddingLeft: 12 }}>
        {offenders.map((o) => (
          <li key={o.stepIndex} style={listItemStyle}>
            • Step {o.stepIndex + 1}: <code>{o.opId}</code>
            {o.referencedFieldName && (<> (references <code>{o.referencedFieldName}</code>)</>)}
          </li>
        ))}
      </ul>
      <p style={mutedParagraphStyle}>
        Remove or replace those steps before promoting.
      </p>
      <div style={buttonRowStyle}>
        <button type="button" style={cancelBtn} onClick={onClose}>Cancel</button>
        <button type="button" style={primaryBtn} onClick={onClose}>Edit chain</button>
      </div>
    </>
  );
}

function NamedChainErrorBody({
  error, onClose, onRetry,
}: { error: NamedChainError; onClose: () => void; onRetry: () => void }) {
  return (
    <>
      <Dialog.Title style={titleStyle}>Couldn't load named chain</Dialog.Title>
      <p style={mutedParagraphStyle}>
        The named chain <code>{error.namedChainId}</code> could not be loaded (status: {error.status}).
        It may have been deleted, or you may have lost network connectivity.
      </p>
      <div style={buttonRowStyle}>
        <button type="button" style={cancelBtn} onClick={onClose}>Cancel</button>
        <button type="button" style={primaryBtn} onClick={onRetry}>Retry</button>
      </div>
    </>
  );
}

function MergeableBody({
  sourceFieldName, newSteps, existingPostOcr, otherDestinations, onClose, onConfirm,
}: {
  sourceFieldName: string;
  newSteps: TransformChainStep[];
  existingPostOcr: Behavior[];
  otherDestinations: OtherDestinationSummary[];
  onClose: () => void;
  onConfirm: (c: PromoteConfirm) => void;
}) {
  const isEmpty = existingPostOcr.length === 0;
  return (
    <>
      <Dialog.Title style={titleStyle}>Promote chain to canonical</Dialog.Title>
      <p style={mutedParagraphStyle}>
        This will move {newSteps.length} transform{newSteps.length === 1 ? '' : 's'} from this destination's
        chain to the field's Post-OCR stage. Every destination mapping <code>{sourceFieldName}</code> will then
        receive the cleaned value.
      </p>

      <div style={sectionLabelStyle}>Existing Post-OCR behaviors</div>
      <div style={listBoxStyle}>
        {isEmpty ? (
          <em style={{ color: 'var(--color-text-muted, #888)' }}>
            (none — this field has no Post-OCR behaviors yet)
          </em>
        ) : (
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            {existingPostOcr.map((b, i) => (
              <li key={`${b.kind}-${b.kind === 'transform' ? b.op : ''}-${i}`} style={listItemStyle}>
                {b.kind === 'transform' ? (<code>{b.op}</code>) : <em>{b.kind}</em>}
              </li>
            ))}
          </ol>
        )}
      </div>

      <div style={sectionLabelStyle}>New steps from this chain</div>
      <div style={listBoxStyle}>
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          {newSteps.map((s, i) => (
            <li key={`${s.type}-${i}`} style={listItemStyle}><code>{s.type}</code></li>
          ))}
        </ol>
      </div>

      {otherDestinations.length > 0 && (
        <>
          <div style={sectionLabelStyle}>Other destinations mapping this field</div>
          <ul style={{ ...listBoxStyle, listStyle: 'none', paddingLeft: 12 }}>
            {otherDestinations.map((d) => (
              <li key={d.destinationId} style={listItemStyle}>
                • <code>{d.destinationName}</code>{' '}
                {d.isNamedRef ? <em>(named chain)</em>
                  : d.chainStepCount === 0 ? <em>(no chain)</em>
                  : <em>({d.chainStepCount} transform{d.chainStepCount === 1 ? '' : 's'})</em>}
              </li>
            ))}
          </ul>
          <p style={mutedParagraphStyle}>
            Their chains will run on the cleaned value — review them after promote.
          </p>
        </>
      )}

      <div style={buttonRowStyle}>
        <button type="button" style={cancelBtn} onClick={onClose}>Cancel</button>
        {isEmpty ? (
          <button type="button" style={primaryBtn} onClick={() => onConfirm({ position: 'append' })}>
            Promote
          </button>
        ) : (
          <>
            <button type="button" style={secondaryBtn} onClick={() => onConfirm({ position: 'prepend' })}>
              Prepend
            </button>
            <button type="button" style={primaryBtn} onClick={() => onConfirm({ position: 'append' })}>
              Append
            </button>
          </>
        )}
      </div>
    </>
  );
}
